export interface DetailedBullet {
  title: string
  detail: string
}

export interface Bonus {
  id: string
  title: string
  subtitle: string
  intro?: string
  bulletsIntro?: string
  bullets?: string[]
  detailedBullets?: DetailedBullet[]
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  secondaryNote?: string
  systemLabel?: string
  system?: string
  result?: string
  closing?: string[]
}

export const bonuses: Bonus[] = [
  {
    id: '01',
    title: 'Workplace Safety Instructor Lesson Planning Handbook',
    subtitle: 'Ready-to-Use Training Plans, Teaching Structures & Classroom Resources',
    intro:
      'A complete professional planning resource created to help instructors prepare structured safety lessons without starting from scratch.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Lesson planning frameworks',
      'Learning objectives',
      'Training needs analysis',
      'Classroom preparation',
      'Teaching sequences',
      'Hazard-identification lessons',
      'Risk assessment lesson planning',
      'Warehouse safety lessons',
      'Machinery safety teaching plans',
      'Fire and electrical awareness lessons',
      'Working-at-height lesson plans',
      'PPE and occupational health lessons',
      'Instructor checklists',
      'Student activity planning',
      'Knowledge checks',
      'Reusable lesson templates',
    ],
    secondaryBulletsIntro: 'Ready-to-Adapt Training Formats',
    secondaryBullets: [
      '15-MINUTE AWARENESS SESSIONS',
      '30-MINUTE FOCUSED LESSONS',
      '60-MINUTE CLASSROOM SESSIONS',
      'EXTENDED TRAINING WORKSHOPS',
    ],
    result:
      'Spend less time building training structures and more time delivering professional safety education.',
  },
  {
    id: '02',
    title: 'Workplace Safety Assessment & Evaluation Toolkit',
    subtitle: 'Knowledge Tests, Competency Rubrics & Training Progress Tracking',
    intro:
      'A professional instructor resource designed to help organize student evaluations, identify learning gaps and assess workplace safety knowledge.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Hazard identification assessments',
      'Risk assessment knowledge tests',
      'Warehouse safety evaluations',
      'Machinery hazard recognition',
      'Fire and electrical awareness questions',
      'Fall prevention knowledge checks',
      'PPE and occupational health assessments',
      'Beginner-level questions',
      'Intermediate-level exercises',
      'Advanced reasoning assessments',
      'Scenario-based evaluations',
      'Instructor answer keys',
      'Knowledge gap identification',
      'Training feedback forms',
      'Progress tracking',
      'Evaluation templates',
    ],
    secondaryBulletsIntro: 'Four Learning Levels',
    secondaryBullets: ['NEEDS GUIDANCE', 'DEVELOPING', 'CONSISTENT', 'STRONG'],
    result:
      'Evaluate learning more consistently and identify which safety concepts require additional explanation.',
  },
  {
    id: '03',
    title: 'Workplace Safety Scenario & Case Study Library',
    subtitle: 'Realistic Workplace Situations, Risk Analysis & Instructor Discussion Guides',
    intro:
      'A professional library of realistic hypothetical workplace scenarios designed to help students analyze hazards, exposure and possible prevention strategies.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Warehouse scenarios',
      'Industrial manufacturing cases',
      'Construction workplace situations',
      'Machinery hazards',
      'Equipment interaction examples',
      'Maintenance-area scenarios',
      'Fire prevention cases',
      'Electrical hazard situations',
      'Working-at-height scenarios',
      'Chemical exposure awareness',
      'Ergonomic risk cases',
      'Occupational health situations',
      'Multi-hazard workplaces',
      'Instructor discussion questions',
      'Risk analysis guidance',
      'Model answers',
      'Follow-up activities',
      'Case study variations',
    ],
    result:
      'Introduce realistic workplace situations into your training without creating every case study independently.',
  },
  {
    id: '04',
    title: 'Workplace Safety Instructor Question Bank',
    subtitle: 'Professional Questions, Answer Keys & Classroom Discussion Prompts',
    intro: 'A comprehensive question library covering the six workplace safety specializations.',
    bulletsIntro: 'Includes:',
    detailedBullets: [
      {
        title: 'HAZARD IDENTIFICATION QUESTIONS',
        detail: 'Hazard types, exposure, consequences and risk assessment.',
      },
      {
        title: 'WAREHOUSE SAFETY QUESTIONS',
        detail: 'Forklifts, pedestrian interactions, material handling and storage.',
      },
      {
        title: 'MACHINERY SAFETY QUESTIONS',
        detail: 'Mechanical hazards, guarding, tools and hazardous energy.',
      },
      {
        title: 'FIRE & ELECTRICAL QUESTIONS',
        detail: 'Fire prevention, electrical awareness and emergency preparedness.',
      },
      {
        title: 'FALL PREVENTION QUESTIONS',
        detail: 'Slips, trips, ladders and working at height.',
      },
      {
        title: 'PPE & OCCUPATIONAL HEALTH QUESTIONS',
        detail: 'Protective equipment, chemical hazards, exposure and ergonomics.',
      },
    ],
    secondaryBulletsIntro: 'Organized Into',
    secondaryBullets: [
      'FOUNDATION',
      'TECHNICAL UNDERSTANDING',
      'HAZARD RECOGNITION',
      'APPLIED SCENARIOS',
      'DISCUSSION & REVIEW',
    ],
    secondaryNote: 'Includes answers and explanations.',
    result:
      'Have a broad collection of professional instructor questions available for training discussions, quizzes and knowledge assessments.',
  },
  {
    id: '05',
    title: 'Workplace Safety Emergency Training Session Vault',
    subtitle: 'Ready-to-Deliver Safety Lessons for Last-Minute Training Sessions',
    intro:
      'Sometimes you need to deliver a safety training session with very little preparation time. Instead of building a new lesson from scratch, this bonus provides complete training structures covering essential workplace safety awareness topics.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Short safety awareness lessons',
      'Hazard identification sessions',
      'Risk assessment lessons',
      'Warehouse safety awareness',
      'Machinery hazard recognition',
      'Hazardous energy awareness',
      'Electrical hazard discussions',
      'Fire prevention lessons',
      'Emergency preparedness topics',
      'Fall prevention awareness',
      'Ladder safety lessons',
      'PPE awareness',
      'Chemical hazard awareness',
      'Ergonomic safety topics',
      'Instructor teaching scripts',
      'Discussion questions',
      'Classroom activities',
      'Knowledge checks',
      'Model answers',
      'Session preparation guides',
    ],
    secondaryBulletsIntro: 'Ready-to-Deliver Formats',
    secondaryBullets: ['15-MINUTE TRAINING SESSIONS', '30-MINUTE TRAINING SESSIONS', '60-MINUTE TRAINING SESSIONS'],
    result:
      'Prepare last-minute workplace safety awareness training more efficiently with organized, instructor-ready lesson resources.',
  },
]
